import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { contractorAuthAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2, 
  Upload, 
  CheckCircle2, 
  Loader2,
  Camera,
  Receipt,
  FileText,
  LogOut,
  HardHat
} from 'lucide-react';
import { toast } from 'sonner';

const ContractorDashboardPage = () => {
  const navigate = useNavigate();
  const [contractorInfo, setContractorInfo] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  // Upload state
  const [uploadType, setUploadType] = useState('photo');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('contractor_token');
    const info = localStorage.getItem('contractor_info');
    const project = localStorage.getItem('contractor_project');
    
    if (!token || !info) {
      navigate('/contractor/login');
      return;
    }
    
    setContractorInfo(JSON.parse(info));
    setProjectInfo(JSON.parse(project));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('contractor_token');
    localStorage.removeItem('contractor_info');
    localStorage.removeItem('contractor_project');
    navigate('/contractor/login');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    const token = localStorage.getItem('contractor_token');
    if (!token) {
      toast.error('Session expired. Please login again.');
      navigate('/contractor/login');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('upload_type', uploadType);
      formData.append('description', description);

      await contractorAuthAPI.upload(formData, token);
      
      setUploadSuccess(true);
      setSelectedFile(null);
      setDescription('');
      toast.success('Upload successful!');
      
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error(error.response?.data?.detail || 'Upload failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!contractorInfo || !projectInfo) {
    return (
      <div className="min-h-screen grid-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="font-bold">{contractorInfo.name}</h1>
              <p className="text-xs text-muted-foreground">{contractorInfo.company || contractorInfo.email}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Project Info */}
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-heading">{projectInfo?.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {projectInfo?.address}, {projectInfo?.city}, {projectInfo?.state}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Form */}
        <Card className="border-border bg-card rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Upload Document</CardTitle>
            <CardDescription>Upload photos, receipts, or permit documents for this project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Type Selection */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'photo', label: 'Photo', icon: Camera },
                { value: 'receipt', label: 'Receipt', icon: Receipt },
                { value: 'permit', label: 'Permit', icon: FileText },
              ].map((type) => (
                <button
                  key={type.value}
                  onClick={() => setUploadType(type.value)}
                  className={`p-4 rounded-sm border transition-all ${
                    uploadType === type.value 
                      ? 'border-amber-500 bg-amber-500/10 text-amber-500' 
                      : 'border-border hover:border-amber-500/50'
                  }`}
                >
                  <type.icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <Label>Select File</Label>
              <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-amber-500/50 transition-colors">
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                  accept={uploadType === 'photo' ? 'image/*' : '*'}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-sm">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Click to select a file</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Kitchen renovation - Day 5"
                className="bg-input/50 border-border focus:border-amber-500 rounded-sm"
              />
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={loading || !selectedFile}
              className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-sm h-12 font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : uploadSuccess ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Uploaded!
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ContractorDashboardPage;
